import { SLICE_TYPE } from "@niivue/niivue"

type TypedVoxelArray =
  | Float32Array
  | Float64Array
  | Int8Array
  | Int16Array
  | Int32Array
  | Uint8Array
  | Uint16Array
  | Uint32Array

/**
 * Minimal NVImage volume interface used by the slice extractor.
 * Models the NVImage methods/properties we need, avoiding a hard import.
 */
export interface NvVolumeLike {
  getVolumeData(voxStart?: number[], voxEnd?: number[], dataType?: string): [TypedVoxelArray, number[]]
  dims?: number[]
  cal_min?: number
  cal_max?: number
}

export interface SliceExtractionParams {
  volume: NvVolumeLike
  sliceType: SLICE_TYPE
  sliceIndex: number
}

export interface SliceExtraction {
  b64img: string
  width: number
  height: number
  sliceIndex: number
  sliceType: SLICE_TYPE
  /** Maps 2D pixel (col, row) back to 3D voxel (x, y, z) */
  pixelToVoxel: (col: number, row: number) => [number, number, number]
}

/**
 * Axis mapping per slice type.
 * For each SLICE_TYPE, defines the through-plane axis and the two in-plane axes.
 * NIfTI dims are 1-indexed: dims[1]=X, dims[2]=Y, dims[3]=Z.
 */
function getSliceAxes(sliceType: SLICE_TYPE): {
  throughDimIdx: number // index into dims[] (1-indexed)
  colDimIdx: number // horizontal axis in the 2D slice
  rowDimIdx: number // vertical axis in the 2D slice
} {
  switch (sliceType) {
    case SLICE_TYPE.AXIAL:
      return { throughDimIdx: 3, colDimIdx: 1, rowDimIdx: 2 } // Z fixed, X=col, Y=row
    case SLICE_TYPE.CORONAL:
      return { throughDimIdx: 2, colDimIdx: 1, rowDimIdx: 3 } // Y fixed, X=col, Z=row
    case SLICE_TYPE.SAGITTAL:
      return { throughDimIdx: 1, colDimIdx: 2, rowDimIdx: 3 } // X fixed, Y=col, Z=row
    default:
      throw new Error(`Unsupported slice type for SAM extraction: ${sliceType}`)
  }
}

/**
 * Build voxStart/voxEnd arrays for getVolumeData to extract a single slice.
 */
function buildSlabBounds(
  dims: number[],
  sliceType: SLICE_TYPE,
  sliceIndex: number
): { voxStart: number[]; voxEnd: number[] } {
  const dimX = (dims[1] ?? 1) - 1
  const dimY = (dims[2] ?? 1) - 1
  const dimZ = (dims[3] ?? 1) - 1

  switch (sliceType) {
    case SLICE_TYPE.AXIAL:
      return { voxStart: [0, 0, sliceIndex], voxEnd: [dimX, dimY, sliceIndex] }
    case SLICE_TYPE.CORONAL:
      return { voxStart: [0, sliceIndex, 0], voxEnd: [dimX, sliceIndex, dimZ] }
    case SLICE_TYPE.SAGITTAL:
      return { voxStart: [sliceIndex, 0, 0], voxEnd: [sliceIndex, dimY, dimZ] }
    default:
      throw new Error(`Unsupported slice type: ${sliceType}`)
  }
}

/**
 * Extract a 2D slice from a NiiVue volume and convert it to a JPEG base64 string
 * suitable for SAM prediction.
 *
 * The returned `pixelToVoxel` function maps 2D pixel coordinates in the slice image
 * back to 3D voxel indices in the volume — used after SAM returns a polygon to
 * write the result into the 3D drawBitmap.
 */
export async function extractSliceAsJpeg(params: SliceExtractionParams): Promise<SliceExtraction> {
  const { volume, sliceType, sliceIndex } = params
  if (!volume?.dims) {
    throw new Error("No volume loaded in viewer")
  }

  const dims = volume.dims
  const axes = getSliceAxes(sliceType)
  const sliceWidth = dims[axes.colDimIdx] ?? 1
  const sliceHeight = dims[axes.rowDimIdx] ?? 1

  // Extract the 1-voxel-thick slab
  const { voxStart, voxEnd } = buildSlabBounds(dims, sliceType, sliceIndex)
  const [voxelData] = volume.getVolumeData(voxStart, voxEnd)

  // Window/level normalization
  const calMin = volume.cal_min ?? 0
  const calMax = volume.cal_max ?? 1
  const range = calMax - calMin || 1

  // Render to OffscreenCanvas
  const canvas = new OffscreenCanvas(sliceWidth, sliceHeight)
  const ctx = canvas.getContext("2d")!
  const imageData = ctx.createImageData(sliceWidth, sliceHeight)
  const pixels = imageData.data

  for (let i = 0; i < sliceWidth * sliceHeight; i++) {
    const raw = voxelData[i] ?? 0
    const gray = Math.max(0, Math.min(255, Math.round(((raw - calMin) / range) * 255)))
    const offset = i * 4
    pixels[offset] = gray
    pixels[offset + 1] = gray
    pixels[offset + 2] = gray
    pixels[offset + 3] = 255
  }

  ctx.putImageData(imageData, 0, 0)

  // Export as JPEG base64
  const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.9 })
  const b64img = await blobToBase64(blob)

  // Build pixelToVoxel mapper
  const fixedSliceIdx = sliceIndex
  const st = sliceType
  const pixelToVoxel = (col: number, row: number): [number, number, number] => {
    switch (st) {
      case SLICE_TYPE.AXIAL:
        return [col, row, fixedSliceIdx]
      case SLICE_TYPE.CORONAL:
        return [col, fixedSliceIdx, row]
      case SLICE_TYPE.SAGITTAL:
        return [fixedSliceIdx, col, row]
      default:
        return [col, row, fixedSliceIdx]
    }
  }

  return {
    b64img,
    width: sliceWidth,
    height: sliceHeight,
    sliceIndex,
    sliceType,
    pixelToVoxel
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  // Chunked encoding to avoid call stack overflow on large slices
  const chunkSize = 0x8000
  const parts: string[] = []
  for (let i = 0; i < bytes.length; i += chunkSize) {
    parts.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)))
  }
  return btoa(parts.join(""))
}

/**
 * Project 3D voxel coordinates from DragReleaseParams onto the 2D slice plane.
 * Returns the bounding box as [x1, y1, x2, y2] in slice-local pixel coordinates.
 */
export function projectVoxelBboxToSlice(
  voxStart: readonly number[],
  voxEnd: readonly number[],
  sliceType: SLICE_TYPE
): { bbox: [number, number, number, number]; sliceIndex: number } {
  const axes = getSliceAxes(sliceType)
  // The through-plane axis index mapped to vec3 indices (0=x, 1=y, 2=z)
  const throughVec3Idx = axes.throughDimIdx - 1
  const colVec3Idx = axes.colDimIdx - 1
  const rowVec3Idx = axes.rowDimIdx - 1

  const x1 = Math.min(voxStart[colVec3Idx]!, voxEnd[colVec3Idx]!)
  const y1 = Math.min(voxStart[rowVec3Idx]!, voxEnd[rowVec3Idx]!)
  const x2 = Math.max(voxStart[colVec3Idx]!, voxEnd[colVec3Idx]!)
  const y2 = Math.max(voxStart[rowVec3Idx]!, voxEnd[rowVec3Idx]!)
  const sliceIndex = Math.round((voxStart[throughVec3Idx]! + voxEnd[throughVec3Idx]!) / 2)

  return {
    bbox: [Math.round(x1), Math.round(y1), Math.round(x2), Math.round(y2)],
    sliceIndex
  }
}

/**
 * Extract non-zero voxel positions from drawBitmap for a given slice,
 * returning them as 2D point coordinates in slice-local space.
 *
 * Used by SAM scribble mode: the user paints on a slice, then we extract
 * the painted voxels as positive point prompts for SAM.
 *
 * @param maxPoints - Subsample to at most this many points (SAM works best with ~20-50).
 *   Points are subsampled uniformly to maintain spatial coverage.
 */
export function extractScribblePoints(
  drawBitmap: Uint8Array,
  volumeDims: [number, number, number],
  sliceType: SLICE_TYPE,
  sliceIndex: number,
  maxPoints = 32
): [number, number][] {
  const [dimX, dimY] = volumeDims
  const strideY = dimX
  const strideZ = dimX * dimY
  const axes = getSliceAxes(sliceType)
  const throughVec3Idx = axes.throughDimIdx - 1
  const colVec3Idx = axes.colDimIdx - 1
  const rowVec3Idx = axes.rowDimIdx - 1

  const colSize = volumeDims[colVec3Idx]!
  const rowSize = volumeDims[rowVec3Idx]!

  // Scan the slice for non-zero voxels
  const allPoints: [number, number][] = []
  for (let row = 0; row < rowSize; row++) {
    for (let col = 0; col < colSize; col++) {
      // Reconstruct 3D voxel index from 2D slice coords
      const vox = [0, 0, 0]
      vox[colVec3Idx] = col
      vox[rowVec3Idx] = row
      vox[throughVec3Idx] = sliceIndex
      const linearIdx = vox[0]! + vox[1]! * strideY + vox[2]! * strideZ

      if (linearIdx >= 0 && linearIdx < drawBitmap.length && drawBitmap[linearIdx] !== 0) {
        allPoints.push([col, row])
      }
    }
  }

  if (allPoints.length <= maxPoints) return allPoints

  // Subsample uniformly
  const step = allPoints.length / maxPoints
  const sampled: [number, number][] = []
  for (let i = 0; i < maxPoints; i++) {
    sampled.push(allPoints[Math.floor(i * step)]!)
  }
  return sampled
}
